import { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { isEmpty, isNil } from 'lodash';
import { useTranslation } from 'react-i18next';
// Import Constants
import { l, ZERO_TIME_WITH_SECONDS } from 'constants/common';
// Import Helpers
import { customMomentWithoutTimezoneConversion } from 'services/Helpers';

const REFRESH_INTERVAL = 1000;
const ZERO_VALUE = '00';

const padTime = (value: number) => String(value).padStart(2, '0');

interface CountdownTimerProps {
    /* Target date in the UTC standard format, e.g. 2026-08-25T18:00:00Z */
    date: Nilable<string>;
    /* Timezone of the target date as a decimal offset, e.g. 4, -5.5 */
    timeZone?: Nilable<number>;
    /* Returned as the remaining time once the target date is reached */
    expiredText?: string;
}

interface CountdownTimer {
    /* The parts are zero padded, so that they can be shown on their own */
    days: string;
    hours: string;
    minutes: string;
    seconds: string;
    hasDays: boolean;
    isExpired: boolean;
    /* The parts combined into a single readable value, e.g. 2 Days 03:25:00 */
    remainingTime: string;
}

const useCountdownTimer = ({ date, timeZone, expiredText = ZERO_TIME_WITH_SECONDS }: CountdownTimerProps) => {
    const { t } = useTranslation();

    // The date is sent as a wall clock reading of its own timezone, so the offset is taken off to get the real moment
    const deadlineTime = useMemo(
        () =>
            isEmpty(date)
                ? null
                : customMomentWithoutTimezoneConversion(date)
                      .subtract(timeZone ?? 0, 'hours')
                      .valueOf(),
        [date, timeZone],
    );

    const getCountdown = useCallback((): CountdownTimer => {
        if (isNil(deadlineTime)) {
            return {
                days: '',
                hours: '',
                minutes: '',
                seconds: '',
                hasDays: false,
                isExpired: false,
                remainingTime: '',
            };
        }

        const duration = moment.duration(deadlineTime - Date.now());

        if (duration.asSeconds() < 1) {
            return {
                days: ZERO_VALUE,
                hours: ZERO_VALUE,
                minutes: ZERO_VALUE,
                seconds: ZERO_VALUE,
                hasDays: false,
                isExpired: true,
                remainingTime: expiredText,
            };
        }

        // asDays is used instead of days, which counts only the days of the last month of the duration
        const totalDays = Math.floor(duration.asDays());
        const hours = padTime(duration.hours());
        const minutes = padTime(duration.minutes());
        const seconds = padTime(duration.seconds());
        const time = `${hours}:${minutes}:${seconds}`;

        return {
            days: padTime(totalDays),
            hours,
            minutes,
            seconds,
            hasDays: totalDays > 0,
            isExpired: false,
            remainingTime: totalDays > 0 ? `${totalDays} ${t(totalDays === 1 ? l.Day : l.Days)} ${time}` : time,
        };
    }, [deadlineTime, expiredText, t]);

    const [countdown, setCountdown] = useState<CountdownTimer>(getCountdown);

    useEffect(() => {
        setCountdown(getCountdown());

        const interval = setInterval(() => {
            const currentCountdown = getCountdown();

            setCountdown(currentCountdown);

            if (currentCountdown.isExpired) {
                clearInterval(interval);
            }
        }, REFRESH_INTERVAL);

        return () => clearInterval(interval);
    }, [getCountdown]);

    return countdown;
};

export default useCountdownTimer;
